/* eslint-disable @typescript-eslint/naming-convention */

// Base payment info metadata interfaces
export interface UpiCollectIntentPaymentMetadata {
    payer_vpa?: string;
    intent_app?: string;
    [key: string]: unknown;
}

export interface CardPaymentMetadata {
    card_payment_info: {
        juspay_redirect_url: string;
        [key: string]: unknown;
    };
}

export interface NetBankingPaymentMetadata {
    netbanking_payment_info: {
        juspay_redirect_url: string;
        [key: string]: unknown;
    };
}

export interface InAppUpiPaymentMetadata {
    in_app_upi_info: {
        account_reference_id: string;
        account_type: string;
    };
}

export interface PhonePePaymentMetadata {
    phonepe_payment_info: {
        phonepe_payment_type: string;
        web_callback_url: string;
    };
}

export interface AmazonPaymentMethodMeta {
    isEnoughBalanceInWallet: string;
    amazon_payment_meta_data?: string;
}

export interface AmazonPayMetadata {
    amazon_payment_info: {
        is_enough_balance_in_wallet: boolean;
        redirect_url: string;
        /** Meta id / encrypted payload from Native Bridge GET_APAY_METADATA */
        meta: string;
    };
}

// Union type for all possible payment metadata
export type GiftingPaymentInfoMethodMetadata =
    | UpiCollectIntentPaymentMetadata
    | CardPaymentMetadata
    | NetBankingPaymentMetadata
    | InAppUpiPaymentMetadata
    | PhonePePaymentMetadata
    | AmazonPayMetadata;

// Payment info interface
export interface GiftingPaymentInfo {
    metadata: GiftingPaymentInfoMethodMetadata & {
        transaction_amount: number | string;
        payment_method: string;
        selected_split_pay_amounts?: Record<string, string>;
        twid_rewards_meta?: Record<string, string>;
    };
}

// Main place order parameters interface
export interface GiftingPlaceOrderRequestParams {
    payment_info: GiftingPaymentInfo;
    source: string;
}

export interface GiftingConfirmOrderRequestParams {
    transaction_id: string;
    order_id: string;
}

// Gifting Order Response Types (already in camelCase from proxy)
export enum TenancyInfoType {
    GIFTING = "BUSINESS_LINE_GIFTING",
}

export interface GiftingTenancyInfo {
    marketplaceId: string;
    businesslineId: TenancyInfoType;
}

export interface GiftingCustomerInfo {
    customerId: string;
}

export interface GiftingDeliveryAddress {
    id: string;
    version: number;
    name: string;
    addressLine1: string;
    addressLine2: string;
    address: string;
    landmark: string;
    area: string;
    mobile: string;
    alternateMobile: string;
    annotation: string;
    instructions: string;
    voiceDirectionsS3Uri: string;
    email: string;
    flatNo: string;
    city: string;
    lat: string;
    lng: string;
    isVerified: boolean;
    reverseGeoCodeFailed: boolean;
}

export interface GiftingVendorInfo {
    name: string;
    id: string;
}

export interface GiftingOrderLine {
    id: string;
    quantity: number;
}

export interface GiftingOrderLines {
    [itemId: string]: GiftingOrderLine;
}

export interface GiftingBlOrderDetail {
    orderId: string;
    tenancyInfo: GiftingTenancyInfo;
    vendorInfo: GiftingVendorInfo;
    orderLines: GiftingOrderLines;
}

export interface GiftingPriceTotal {
    totalItemPrice: number;
    totalFeePrice: number;
    totalDiscount: number;
    totalOptinCharges: number;
    totalTax: number;
    finalPrice: number;
}

export interface GiftingFeePrice {
    feeType: string;
    finalFee: number;
    baseFee: number;
    totalFeeDiscount: number;
}

export interface GiftingOptinCharge {
    chargeType: string;
    amount: number;
}

export interface GiftingOfferResponse {
    couponCodes: string[];
}

export interface GiftingPricingInfo {
    priceTotal: GiftingPriceTotal;
    feePrices: GiftingFeePrice[];
    optinCharges: GiftingOptinCharge[];
    offerResponse: GiftingOfferResponse;
}

export interface GiftingPaymentInfoResponse {
    paymentRedirectionInfo: string;
    marketplaceReferenceId: string;
    paymentMethod: string;
}

export interface GiftingTickScreenComms {
    header: string;
    subHeader: string;
}

export interface GiftingPresentationDetails {
    tickScreenComms: GiftingTickScreenComms;
}

export interface GiftingOrderResponseData {
    orderId: string;
    tenancyInfo: GiftingTenancyInfo;
    customerInfo: GiftingCustomerInfo;
    deliveryAddress: GiftingDeliveryAddress;
    blOrderDetails: GiftingBlOrderDetail[];
    pricingInfo: GiftingPricingInfo;
    paymentInfo: GiftingPaymentInfoResponse;
    presentationDetails: GiftingPresentationDetails;
}

// Transformer response interface
export interface GiftingTransformerResponse<T> {
    statusCode: number;
    statusMessage?: string;
    data?: T;
    rawResponse?: unknown;
    [key: string]: unknown;
}

// Raw response interfaces (snake_case as received from API)
export interface GiftingRawTenancyInfo {
    marketplace_id: string;
    businessline_id: string;
}

export interface GiftingRawCustomerInfo {
    customer_id: string;
}

export interface GiftingRawDeliveryAddress {
    id: string;
    version: number;
    name: string;
    address_line1: string;
    address_line2: string;
    address: string;
    landmark: string;
    area: string;
    mobile: string;
    alternate_mobile: string;
    annotation: string;
    instructions: string;
    voice_directions_s3_uri: string;
    email: string;
    flat_no: string;
    city: string;
    lat: string;
    lng: string;
    is_verified: boolean;
    reverse_geo_code_failed: boolean;
}

export interface GiftingRawVendorInfo {
    name: string;
    id: string;
}

export interface GiftingRawOrderLine {
    id: string;
    quantity: number;
}

export interface GiftingRawOrderLines {
    [itemId: string]: GiftingRawOrderLine;
}

export interface GiftingRawBlOrderDetail {
    order_id: string;
    tenancy_info: GiftingRawTenancyInfo;
    vendor_info: GiftingRawVendorInfo;
    order_lines: GiftingRawOrderLines;
}

export interface GiftingRawPriceTotal {
    total_item_price: number;
    total_fee_price: number;
    total_discount: number;
    total_optin_charges: number;
    total_tax: number;
    final_price: number;
}

export interface GiftingRawFeePrice {
    fee_type: string;
    final_fee: number;
    base_fee: number;
    total_fee_discount: number;
}

export interface GiftingRawOptinCharge {
    charge_type: string;
    amount: number;
}

export interface GiftingRawOfferResponse {
    coupon_codes: string[];
}

export interface GiftingRawPricingInfo {
    price_total: GiftingRawPriceTotal;
    fee_prices: GiftingRawFeePrice[];
    optin_charges: GiftingRawOptinCharge[];
    offer_response: GiftingRawOfferResponse;
}

export interface GiftingRawPaymentInfo {
    payment_redirection_info: string;
    marketplace_reference_id: string;
    payment_method: string;
}

export interface GiftingRawTickScreenComms {
    header: string;
    sub_header: string;
}

export interface GiftingRawPresentationDetails {
    tick_screen_comms: GiftingRawTickScreenComms;
}

export interface GiftingRawOrderResponseData {
    order_id: string;
    tenancy_info: GiftingRawTenancyInfo;
    customer_info: GiftingRawCustomerInfo;
    delivery_address: GiftingRawDeliveryAddress;
    bl_order_details: GiftingRawBlOrderDetail[];
    pricing_info: GiftingRawPricingInfo;
    payment_info: GiftingRawPaymentInfo;
    presentation_details: GiftingRawPresentationDetails;
}

export interface GiftingRawOrderApiResponse {
    statusCode: number;
    statusMessage?: string;
    data?: GiftingRawOrderResponseData;
}
